/**
 * useAssociatedRecordsBinder — binding del picker "Associa ai record"
 * a un'entità in 2 modalità:
 *
 *  - `create`: state controllato locale, applicazione al submit con
 *     `createBidirectionalRelationship` (entità+Relationship batch).
 *     Usato in *FormModal (TaskForm modal mode, NoteFormModal,
 *     CallFormModal, MeetingFormModal). Dispatcha al global slice
 *     `state.relationships` perché il flusso create avviene tipicamente
 *     dentro una pagina (Person/Business Detail) che vuole vedere
 *     subito la nuova associazione nei propri pannelli.
 *
 *  - `edit`: scope COMPLETAMENTE LOCALE — lista esistente via
 *     `relationshipApi.getRelationshipsFromContributors` diretto (no dispatch),
 *     create/delete via `relationshipApi.createBidirectional` /
 *     `deleteBidirectional` diretti con update locale. Lo slice globale
 *     `state.relationships` NON viene mai toccato in questa modalità.
 *     Questo isolamento è necessario perché lo slice è source-of-truth
 *     per un *singolo* parent (es. PersonDetail) ed è
 *     contemporaneamente popolato dai suoi RelationPanel; un binder
 *     embed in una timeline che mostra Task/Note/Call/Meeting non deve
 *     contaminarlo (vedi bug: le associazioni create dall'embed
 *     comparivano nella colonna del parent contact, e
 *     `fetchRelationships` del child sovrascriveva lo state del
 *     parent). La controparte: i pannelli del parent non vedono in
 *     tempo reale le associazioni create/cancellate dall'embed — è una
 *     scelta consapevole (gli scope sono ortogonali: il pannello del
 *     Person mostra le rel del Person, l'embed mostra le rel del
 *     Task/Note/Call/Meeting espanso).
 *
 * Comune: `useRelationTargetEntityTypes` + `useAssociatedRecordsPicker`
 * incapsulati qui. I tipi associabili non sono piu' una costante locale
 * (`CORE_ASSOCIABLE_TYPES`, TS-80): li dichiarano le entita' stesse col
 * flag `relationTarget`, core e plugin allo stesso modo.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { useCurrentGroupId } from '../auth/authSelectors';
import { useToast } from './useToast';
import { useAssociatedRecordsPicker, type AssociatedRecord } from './useAssociatedRecordsPicker';
import { useRelationTargetEntityTypes } from '../plugin/useEnabledEntityTypes';
import { createBidirectionalRelationship } from '../entities/relationship/relationshipThunks';
import { relationshipApi, ERROR_DUPLICATE_RELATIONSHIP } from '../entities/relationship/relationshipApi';
import { getEntityShortNameByFqn } from '../entityRegistry/entityRegistry';

interface ExtraLink {
  className: string;
  classPK: number;
}

/**
 * Record associato in edit-mode, arricchito col `relationshipId` (uso interno
 * del binder, serve alla cancellazione). Struttura compatibile con
 * AssociatedRecord: il widget legge solo typeName/fqn/id/label.
 */
type LinkedRecord = AssociatedRecord & { relationshipId: number };

type CreateModeInput = {
  mode: 'create';
  /**
   * FQN dell'entità che si sta creando (es. "it.dvel.crm.core.model.Note").
   * Serve al fetch del preview di pre-associazione. Opzionale: se assente non
   * viene seminata alcuna pre-associazione.
   */
  entityFqn?: string;
  /**
   * Entità di contesto da cui si è aperta la creazione (es. la Person/Business
   * del Detail). Alimenta il preview: i contributor decidono cosa pre-associare
   * al nuovo record (vedi RelationshipContributor.getPreviewRelationships lato
   * backend). null/assente (creazione da lista globale) = nessun preview.
   */
  reference?: { className: string; classPK: number } | null;
};

type EditModeInput = {
  mode: 'edit';
  /** FQN dell'entità sotto edit (es. "it.dvel.crm.core.model.Task"). */
  entityFqn: string;
  /** ID dell'entità sotto edit. */
  entityId: number;
};

export type AssociatedRecordsBinderInput = CreateModeInput | EditModeInput;

/** Subset di props pronto per essere spread su `<AssociatedRecordsField />`. */
export interface AssociatedRecordsPickerProps {
  recordOptions: AssociatedRecord[];
  recordsLoading: boolean;
  recordsAutocompleteProps: ReturnType<typeof useAssociatedRecordsPicker>['autocompleteProps'];
  recordsListboxProps: ReturnType<typeof useAssociatedRecordsPicker>['listboxProps'];
  /** Tutti i type associabili (core + plugin), per la lista tipi a sinistra
   *  nel popover "Associazioni" — include anche tipi con 0 selezionati. */
  associableTypeNames: readonly string[];
  /**
   * Notifica il tipo attualmente attivo nel pannello, per restringere la
   * ricerca server-side: `<typeName>` cerca solo su quel tipo, `null` (vista
   * "Selezionate") disattiva la ricerca. Il pannello la chiama al cambio di
   * filtro; i display mode senza filtro per tipo (always-open) non la usano.
   */
  onActiveTypeChange: (typeName: string | null) => void;
}

export interface AssociatedRecordsBinderOutput {
  /** create: state controllato | edit: lista esistente derivata dal summary. */
  records: AssociatedRecord[];
  /** Spread su `<AssociatedRecordsField />`. */
  pickerProps: AssociatedRecordsPickerProps;

  // ── create mode ──
  /** Setter del state controllato. Definito solo in create mode. */
  setRecords?: (records: AssociatedRecord[]) => void;
  /**
   * Esegue al submit i `createBidirectionalRelationship` per ogni record
   * scelto. `extraLinks` aggiunge link addizionali (es. `linkedEntity` del
   * call site PersonDetail/BusinessDetail). Definito solo in create mode.
   */
  applyAtSubmit?: (
    sourceFqn: string,
    sourceId: number,
    extraLinks?: ExtraLink[],
  ) => Promise<void>;

  // ── edit mode ──
  /** Flag "salvataggio in corso" per disable UI. Definito solo in edit mode. */
  recordsSaving?: boolean;
  /**
   * Diff add/removed su newValue vs records correnti, dispatch immediato di
   * create/delete Relationship. Definito solo in edit mode.
   */
  onRecordsChange?: (newValue: AssociatedRecord[]) => Promise<void>;
}

export function useAssociatedRecordsBinder(
  input: AssociatedRecordsBinderInput,
): AssociatedRecordsBinderOutput {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const groupId = useCurrentGroupId();

  // ── parte comune (entrambe le modalità) ──────────────────────────────
  const relationTargetEntityTypes = useRelationTargetEntityTypes();
  const associableTypeNames = useMemo(
    () => relationTargetEntityTypes.map((et) => et.typeName),
    [relationTargetEntityTypes],
  );

  // Tipo attivo per restringere la ricerca: `undefined` = tutti i tipi,
  // `null` = nessuna ricerca, `<type>` = solo quel tipo. Pilotato dal pannello
  // via onActiveTypeChange. Default per modalità:
  //  - edit (pannello click-to-edit) → null: nessuna fetch finché l'utente non
  //    apre il popover e sceglie un tipo (evita la fetch multi-tipo sprecata
  //    quando il popover resta chiuso);
  //  - create (always-open) → undefined: ricerca multi-tipo sempre attiva.
  const [activeTypeName, setActiveTypeName] = useState<string | null | undefined>(
    input.mode === 'edit' ? null : undefined,
  );

  const {
    items: recordOptions,
    loading: recordsLoading,
    autocompleteProps: recordsAutocompleteProps,
    listboxProps: recordsListboxProps,
  } = useAssociatedRecordsPicker({ associableTypeNames, groupId, activeTypeName });

  const pickerProps: AssociatedRecordsPickerProps = {
    recordOptions,
    recordsLoading,
    recordsAutocompleteProps,
    recordsListboxProps,
    associableTypeNames,
    onActiveTypeChange: setActiveTypeName,
  };

  // ── create mode ──────────────────────────────────────────────────────
  // Hook chiamati sempre (rule-of-hooks), il loro output viene esposto
  // condizionalmente in base alla modalità.
  const [selectedRecords, setSelectedRecords] = useState<AssociatedRecord[]>([]);

  // Primitivi estratti per le deps dell'effetto (input è ricreato ad ogni render).
  const createEntityFqn = input.mode === 'create' ? input.entityFqn ?? '' : '';
  const createRefClassName = input.mode === 'create' ? input.reference?.className ?? '' : '';
  const createRefClassPK = input.mode === 'create' ? input.reference?.classPK ?? 0 : 0;

  // Seeding del preview. Dato il tipo in creazione + l'entità di contesto, il
  // backend (RelationshipContributor.getPreviewRelationships) decide cosa
  // pre-associare; qui mappiamo il summary in AssociatedRecord e lo mettiamo
  // come selezione iniziale del widget. L'utente può poi modificarla; al submit
  // `applyAtSubmit` linka esattamente la selezione (niente extraLinks).
  //
  // L'effetto dipende solo da identificatori STABILI per la vita del modale
  // (entityFqn/reference/groupId), quindi gira una volta sola (due in StrictMode,
  // entrambe applicano lo stesso risultato) e non sovrascrive le modifiche utente.
  // `associableTypeNames` è letto via ref sempre aggiornata, così l'arrivo
  // asincrono dei tipi plugin non rilancia l'effetto (che ri-seminerebbe).
  const associableTypeNamesRef = useRef(associableTypeNames);
  associableTypeNamesRef.current = associableTypeNames;

  useEffect(() => {
    if (input.mode !== 'create') return;
    if (!createEntityFqn || !createRefClassName || !createRefClassPK || !groupId) return;
    let cancelled = false;
    relationshipApi
      .getPreviewRelationshipsFromContributors(
        groupId, createEntityFqn, createRefClassName, createRefClassPK)
      .then((result) => {
        if (cancelled) return;
        const allowed = associableTypeNamesRef.current;
        const records: AssociatedRecord[] = [];
        for (const contributor of result.contributors ?? []) {
          const typeName = getEntityShortNameByFqn(contributor.className);
          if (!typeName || !allowed.includes(typeName)) continue;
          for (const entry of contributor.entries ?? []) {
            records.push({
              typeName,
              fqn: contributor.className,
              id: entry.classPK,
              label: entry.title,
              subtitle: entry.description ?? undefined,
            });
          }
        }
        if (records.length > 0) setSelectedRecords(records);
      })
      .catch((err) => {
        if (!cancelled) {
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [input.mode, createEntityFqn, createRefClassName, createRefClassPK, groupId, toast]);

  const applyAtSubmit = useCallback(
    async (sourceFqn: string, sourceId: number, extraLinks: ExtraLink[] = []) => {
      const toLink: ExtraLink[] = [...extraLinks];
      for (const rec of selectedRecords) toLink.push({ className: rec.fqn, classPK: rec.id });
      for (const entity of toLink) {
        try {
          await dispatch(
            createBidirectionalRelationship({
              className1: sourceFqn,
              classPK1: sourceId,
              className2: entity.className,
              classPK2: entity.classPK,
              descriptionForward: null,
              descriptionReverse: null,
            }),
          ).unwrap();
        } catch (err) {
          // best-effort: continuiamo con gli altri link e accumuliamo
          // solo l'ultimo errore (coerente con pattern TaskForm originale).
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
    },
    [dispatch, selectedRecords, toast],
  );

  // ── edit mode (scope completamente locale, no global slice) ──────────
  const editEntityFqn = input.mode === 'edit' ? input.entityFqn : '';
  const editEntityId = input.mode === 'edit' ? input.entityId : 0;

  // Le associazioni esistenti vengono dal summary (una sola GET, dati gia'
  // leggibili). Ogni record porta il relationshipId per la cancellazione,
  // oltre a typeName/fqn/id/label per il widget.
  const [linked, setLinked] = useState<LinkedRecord[]>([]);

  useEffect(() => {
    if (input.mode !== 'edit') return;
    if (!editEntityFqn || !editEntityId || !groupId) return;
    let cancelled = false;
    relationshipApi
      .getRelationshipsFromContributors(groupId, editEntityFqn, editEntityId)
      .then((result) => {
        if (cancelled) return;
        const records: LinkedRecord[] = [];
        for (const contributor of result.contributors ?? []) {
          const typeName = getEntityShortNameByFqn(contributor.className);
          if (!typeName || !associableTypeNames.includes(typeName)) continue;
          for (const entry of contributor.entries ?? []) {
            records.push({
              typeName,
              fqn: contributor.className,
              id: entry.classPK,
              label: entry.title,
              relationshipId: entry.relationshipId,
            });
          }
        }
        setLinked(records);
      })
      .catch((err) => {
        if (!cancelled) {
          toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [input.mode, editEntityFqn, editEntityId, groupId, associableTypeNames, toast]);

  const [recordsSaving, setRecordsSaving] = useState(false);

  const onRecordsChange = useCallback(
    async (newValue: AssociatedRecord[]) => {
      if (input.mode !== 'edit' || !editEntityFqn || !groupId) return;
      const sameKey = (a: AssociatedRecord, b: AssociatedRecord) =>
        a.typeName === b.typeName && a.id === b.id;
      const added = newValue.filter((n) => !linked.some((s) => sameKey(s, n)));
      const removed = linked.filter((s) => !newValue.some((n) => sameKey(s, n)));

      setRecordsSaving(true);
      try {
        for (const rec of added) {
          try {
            const [direct] = await relationshipApi.createBidirectional(groupId, {
              className1: editEntityFqn,
              classPK1: editEntityId,
              className2: rec.fqn,
              classPK2: rec.id,
              descriptionForward: null,
              descriptionReverse: null,
            });
            setLinked((prev) => [
              ...prev,
              {
                typeName: rec.typeName,
                fqn: rec.fqn,
                id: rec.id,
                label: rec.label,
                relationshipId: direct.relationshipId,
              },
            ]);
          } catch (err) {
            if (err instanceof Error && err.message === ERROR_DUPLICATE_RELATIONSHIP) {
              toast({ severity: 'warning', message: t('relationship.errorDuplicate') });
            } else {
              throw err;
            }
          }
        }
        // `removed` arriva da `linked`, quindi ogni record porta gia' il
        // relationshipId: la delete bidirezionale ricostruisce il payload da
        // sorgente (editEntity*) + target (rec.fqn/rec.id).
        for (const rec of removed) {
          const deletedIds = await relationshipApi.deleteBidirectional(groupId, {
            relationshipId: rec.relationshipId,
            className1: editEntityFqn,
            classPK1: editEntityId,
            className2: rec.fqn,
            classPK2: rec.id,
          });
          const deletedSet = new Set(deletedIds);
          setLinked((prev) => prev.filter((r) => !deletedSet.has(r.relationshipId)));
        }
        if (added.length + removed.length > 0) {
          toast({ severity: 'success', message: t('common.saved') });
        }
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
      } finally {
        setRecordsSaving(false);
      }
    },
    [input.mode, editEntityFqn, editEntityId, groupId, linked, toast, t],
  );

  // ── output (discriminato per modalità) ───────────────────────────────
  if (input.mode === 'create') {
    return {
      records: selectedRecords,
      pickerProps,
      setRecords: setSelectedRecords,
      applyAtSubmit,
    };
  }
  return {
    records: linked,
    pickerProps,
    recordsSaving,
    onRecordsChange,
  };
}

export type { AssociatedRecord } from './useAssociatedRecordsPicker';
